import { useMemo } from "react";

import classNames from "../../utils/classNames";
import { compareDistrictNames } from "../../utils/postcodeDistricts";
import CoverageMap from "./CoverageMap";
import { WizardState } from "./types";

interface CoverageStepProps {
  state: WizardState;
  onToggle: (district: string) => void;
  onSelectAll: (selected: boolean) => void;
}

export default function CoverageStep({ state, onToggle, onSelectAll }: CoverageStepProps) {
  const sorted = useMemo(
    () => [...state.districts].sort((a, b) => compareDistrictNames(a.district, b.district)),
    [state.districts]
  );
  const selectedCount = sorted.filter((d) => d.selected).length;

  if (!state.yardLatLng) {
    return <p className="text-sm text-gray-600">Go back and enter your yard postcode first.</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Check your coverage area</h2>
        <p className="mt-1 text-sm text-gray-600">
          These are the postcode districts within {state.radiusMiles} miles of {state.yardPostcode}. Click a district
          on the map or in the list to remove it if you don't collect from there.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CoverageMap
          className="lg:col-span-2 h-[420px] lg:h-[560px]"
          centre={state.yardLatLng}
          radiusMiles={state.radiusMiles}
          districts={state.districts}
          onSelect={onToggle}
        />

        <div className="flex h-[420px] flex-col rounded-lg border border-gray-200 lg:h-[560px]">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">
              {selectedCount} of {sorted.length} districts
            </span>
            <div className="flex gap-2 text-xs">
              <button type="button" className="text-primary-600 hover:underline" onClick={() => onSelectAll(true)}>
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button type="button" className="text-primary-600 hover:underline" onClick={() => onSelectAll(false)}>
                Clear
              </button>
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {sorted.map((d) => (
              <li key={d.district}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={d.selected}
                    onChange={() => onToggle(d.district)}
                  />
                  <span className={classNames("w-14 text-sm font-semibold", d.selected ? "text-gray-900" : "text-gray-400 line-through")}>
                    {d.district}
                  </span>
                  <span className="flex-1 truncate text-sm text-gray-500">{d.town ?? ""}</span>
                  <span className="text-xs text-gray-400">{d.distanceMiles.toFixed(1)} mi</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
